import { IconName } from '@blueprintjs/icons';
import React, { useState } from 'react';
import {
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  FormattedTime,
} from 'react-intl';
import Box from 'ui-box';
import {
  Button,
  Card,
  defaultColors,
  defaultFills,
  getColorFromIntent,
  Icon,
  Intents,
  majorScale,
  minorScale,
} from '../../npaper';
import { ITimelineLog } from '../../stores/TimelineLog';

export interface ITimeLineItemProps {
  timelineLog: ITimelineLog;
}

const EmailMetadata = ({
  metadata,
}: {
  metadata: {
    pdf_url?: string;
    opens?: { by: string; timestamp: string; first_open?: boolean }[];
    delivers?: { to: string; timestamp: string }[];
    bounces?: {
      recipient: string;
      inactive: boolean;
      type: string;
      description: string;
      can_activate: boolean;
      bounce_id: number;
      timestamp: string;
    }[];
    complaints?: {
      recipient: string;
      timestamp: string;
      description: string;
    }[];
  };
}) => {
  return !metadata.pdf_url &&
    !metadata.opens &&
    !metadata.delivers &&
    !metadata.complaints &&
    !metadata.bounces ? (
    <Box className="p muted" marginTop={majorScale(1)}>
      <FormattedMessage defaultMessage="Nothing new to report" />
    </Box>
  ) : (
    <Box marginTop={majorScale(2)}>
      {metadata.bounces &&
        Array.from(metadata.bounces)
          .reverse()
          .map((bounce, i) => (
            <Box
              key={i}
              className="p muted"
              marginTop={majorScale(1)}
              display="flex"
              alignItems="center"
            >
              <Icon
                icon="error"
                marginRight={majorScale(1)}
                color={defaultFills.danger[500]}
              />
              <FormattedMessage
                defaultMessage="{bounceType} from {email} on {timestamp, date, medium} {timestamp, time, short}"
                values={{
                  bounceType: bounce.type,
                  email: bounce.recipient,
                  timestamp: new Date(bounce.timestamp || Date.now()),
                }}
              />
            </Box>
          ))}
      {metadata.complaints &&
        Array.from(metadata.complaints)
          .reverse()
          .map((complaint, i) => (
            <Box
              key={i}
              className="p muted"
              marginTop={majorScale(1)}
              display="flex"
              alignItems="center"
            >
              <Icon
                icon="error"
                marginRight={majorScale(1)}
                color={defaultFills.danger[500]}
              />
              <FormattedMessage
                defaultMessage="Spam complaint from {email} on {timestamp, date, medium} {timestamp, time, short}"
                values={{
                  email: complaint.recipient,
                  timestamp: new Date(complaint.timestamp || Date.now()),
                }}
              />
            </Box>
          ))}
      {metadata.opens &&
        Array.from(metadata.opens)
          .reverse()
          .map((open, i) => (
            <Box
              key={i}
              className="p muted"
              marginTop={majorScale(1)}
              display="flex"
              alignItems="center"
            >
              <Icon icon="eye-open" marginRight={majorScale(1)} />
              <FormattedMessage
                defaultMessage="Opened by {email} on {timestamp, date, medium} {timestamp, time, short}"
                values={{
                  email: open.by,
                  timestamp: new Date(open.timestamp),
                }}
              />
            </Box>
          ))}
      {metadata.delivers &&
        Array.from(metadata.delivers)
          .reverse()
          .map((deliver, i) => (
            <Box
              key={i}
              className="p muted"
              marginTop={majorScale(1)}
              display="flex"
              alignItems="center"
            >
              <Icon icon="inbox" marginRight={majorScale(1)} />
              <FormattedMessage
                defaultMessage="Delivered to {email} on {timestamp, date, medium} {timestamp, time, short}"
                values={{
                  email: deliver.to,
                  timestamp: new Date(deliver.timestamp),
                }}
              />
            </Box>
          ))}
      {metadata.pdf_url && (
        <Box
          className="p muted"
          marginTop={majorScale(1)}
          display="flex"
          alignItems="center"
        >
          <Icon icon="document" marginRight={majorScale(1)} />
          <a
            className="link a"
            href={metadata.pdf_url}
            target="_blank"
            rel="noreferrer"
          >
            <FormattedMessage defaultMessage="Download the attached PDF file" />
          </a>
        </Box>
      )}
    </Box>
  );
};

const computeUpdatedMessage = (metadata: {
  updated?: { old?: any; new?: any };
}) => {
  if (metadata.updated && metadata.updated.old) {
    let tokens = Object.keys(metadata.updated.old).map((key, i, arr) => (
      <React.Fragment key={i}>
        <FormattedMessage
          defaultMessage="{key} from <before>{old}</before> to <after>{new}</after>"
          values={{
            key,
            old: metadata.updated!.old[key] || "''",
            new: metadata.updated!.new[key] || "''",
            before: (content: any) => (
              <Box is="span" fontStyle="italic" textDecoration="line-through">
                {content}
              </Box>
            ),
            after: (content: any) => (
              <Box is="span" fontStyle="italic">
                {content}
              </Box>
            ),
          }}
        />
        {i !== arr.length - 1 && ', '}
      </React.Fragment>
    ));
    return (
      <>
        <FormattedMessage defaultMessage="changed" /> {tokens}
      </>
    );
  }
  return 'updated the item';
};

const actionMapping: {
  [key: string]: {
    icon: IconName;
    intent?: Intents;
    RenderMetadata?: React.FC<ITimelineLog>;
    RenderChildren?: React.FC<ITimelineLog>;
  };
} = {
  updated: {
    icon: 'edit',
    RenderMetadata: (item) => <>{computeUpdatedMessage(item.metadata)}</>,
  },
  created: {
    icon: 'add',
    RenderMetadata: (item) => (
      <FormattedMessage defaultMessage="created the item" />
    ),
  },
};

const specificActionMapping: typeof actionMapping = {
  // Sales: Offers
  'sale.offer.opened': {
    icon: 'tick-circle',
    intent: 'none',
    RenderMetadata: (item) => (
      <FormattedMessage defaultMessage="opened the offer" />
    ),
  },
  'sale.offer.sent': {
    icon: 'send-message',
    intent: 'none',
    RenderMetadata: (item) => {
      return (
        <FormattedMessage
          defaultMessage="sent the offer to {recipients}"
          values={{
            recipients: item.metadata.to && item.metadata.to.join(', '),
          }}
        />
      );
    },
    RenderChildren: (item) => <EmailMetadata metadata={item.metadata} />,
  },
  // Sales: Orders
  'sale.order.placed': {
    icon: 'shopping-cart',
    intent: 'info',
    RenderMetadata: () => (
      <FormattedMessage defaultMessage="marked the order as placed" />
    ),
  },
  'sale.order.sent': {
    icon: 'send-message',
    intent: 'none',
    RenderMetadata: (item) => (
      <FormattedMessage
        defaultMessage="sent an order confirmation to {recipients}"
        values={{
          recipients: item.metadata.to && item.metadata.to.join(', '),
        }}
      />
    ),
    RenderChildren: (item) => <EmailMetadata metadata={item.metadata} />,
  },
  'sale.order.voided': {
    icon: 'trash',
    intent: 'danger',
  },
  // Sales: Invoices
  'sale.invoice.created': {
    icon: 'dollar',
    intent: 'success',
    RenderMetadata: (item) => (
      <FormattedMessage
        defaultMessage="created invoice {invoice_number}"
        values={{ invoice_number: item.metadata.invoice_number }}
      />
    ),
  },
  'sale.invoice.confirmed': {
    icon: 'endorsed',
    intent: 'success',
    RenderMetadata: (item) => (
      <FormattedMessage defaultMessage="confirmed the invoice" />
    ),
  },
  'sale.invoice.voided': {
    icon: 'trash',
    intent: 'danger',
  },
  'sale.invoice.sent': {
    icon: 'send-message',
    intent: 'none',
    RenderMetadata: (item) => {
      return (
        <FormattedMessage
          defaultMessage="sent the invoice to {recipients}"
          values={{
            recipients: item.metadata.to && item.metadata.to.join(', '),
          }}
        />
      );
    },
    RenderChildren: (item) => <EmailMetadata metadata={item.metadata} />,
  },
  'sale.credit-note.sent': {
    icon: 'send-message',
    intent: 'none',
    RenderMetadata: (item) => {
      return (
        <FormattedMessage
          defaultMessage="sent the credit note to {recipients}"
          values={{
            recipients: item.metadata.to && item.metadata.to.join(', '),
          }}
        />
      );
    },
    RenderChildren: (item) => <EmailMetadata metadata={item.metadata} />,
  },

  // Purchase Orders
  'purchaseorder.sent': {
    icon: 'send-message',
    intent: 'none',
    RenderMetadata: (item) => (
      <FormattedMessage
        defaultMessage="sent the purchase order to {recipients}"
        values={{
          recipients: item.metadata.to && item.metadata.to.join(', '),
        }}
      />
    ),
    RenderChildren: (item) => <EmailMetadata metadata={item.metadata} />,
  },
  'purchaseorder.placed': {
    icon: 'shopping-cart',
    intent: 'info',
    RenderMetadata: (item) => (
      <FormattedMessage defaultMessage="marked the purchase order as placed" />
    ),
  },
  'purchaseorder.finalized': {
    icon: 'endorsed',
    intent: 'success',
    RenderMetadata: (item) => (
      <FormattedMessage defaultMessage="finalized the purchase order" />
    ),
  },
  'purchaseorder.voided': {
    icon: 'error',
    intent: 'danger',
    RenderMetadata: (item) => (
      <FormattedMessage defaultMessage="voided the purchase order" />
    ),
  },
  'purchaseorder.items.updated': {
    icon: 'edit',
    intent: 'none',
    RenderMetadata: (item) => {
      return (
        <FormattedMessage
          defaultMessage="updated the items"
          values={{
            sku: item.metadata.sku,
          }}
        />
      );
    },
    RenderChildren: (item) => {
      return (
        <Card overflow="hidden">
          <Box
            display="grid"
            gridAutoFlow="column"
            gridAutoColumns="1fr"
            paddingY={minorScale(1.5)}
            paddingX={minorScale(2)}
          >
            <p>
              <FormattedMessage defaultMessage="Product SKU" />
            </p>
            <p>
              <FormattedMessage defaultMessage="Quantity" />
            </p>
            <p>
              <FormattedMessage defaultMessage="Cost price" />
            </p>
            <p>
              <FormattedMessage defaultMessage="Quantity completed" />
            </p>
            <p>
              <FormattedMessage defaultMessage="Quantity cancelled" />
            </p>
          </Box>
          {item.metadata.items &&
            Object.keys(item.metadata.items).map((key) => {
              const poi = item.metadata.items[key];
              return (
                <Box key={key}>
                  {poi.old && (
                    <Box
                      display="grid"
                      gridAutoFlow="column"
                      gridAutoColumns="1fr"
                      paddingY={minorScale(1)}
                      paddingX={minorScale(2)}
                      backgroundColor={defaultColors.red[50]}
                      className="muted"
                      textDecoration="line-through"
                    >
                      <p>{poi.sku}</p>
                      <p className="font-mono">{poi.old.quantity}</p>
                      <p className="font-mono">
                        <FormattedNumber
                          format="currency"
                          currency={poi.old.cost_price_currency}
                          value={poi.old.cost_price}
                        />
                      </p>
                      <p className="font-mono">{poi.old.quantity_completed}</p>
                      <p className="font-mono">{poi.old.quantity_cancelled}</p>
                    </Box>
                  )}
                  {poi.new && (
                    <Box
                      display="grid"
                      gridAutoFlow="column"
                      gridAutoColumns="1fr"
                      paddingY={minorScale(1)}
                      paddingX={minorScale(2)}
                      backgroundColor={defaultColors.green[50]}
                      className="muted"
                    >
                      <p>{poi.sku}</p>
                      <p className="font-mono">{poi.new.quantity}</p>
                      <p className="font-mono">
                        <FormattedNumber
                          format="currency"
                          currency={poi.new.cost_price_currency}
                          value={poi.new.cost_price}
                        />
                      </p>
                      <p className="font-mono">{poi.new.quantity_completed}</p>
                      <p className="font-mono">{poi.new.quantity_cancelled}</p>
                    </Box>
                  )}
                </Box>
              );
            })}
        </Card>
      );
    },
  },
};

export const TimeLineItem: React.FC<ITimeLineItemProps> = ({ timelineLog }) => {
  const mapping =
    specificActionMapping[timelineLog.event_type] ||
    actionMapping[timelineLog.action || ''];
  const intent: Intents | false =
    mapping && mapping.intent ? mapping.intent : false;

  const [showDetails, setShowDetails] = useState(false);
  return (
    <Box className="time-line-item">
      <Icon
        icon={mapping ? mapping.icon : 'circle'}
        color={!intent ? defaultFills.muted : getColorFromIntent(intent)}
      />
      <Box>
        <Box display="flex">
          <p>
            <span className="medium">{timelineLog.user_name}</span>{' '}
            <span className="muted">
              {mapping && mapping.RenderMetadata ? (
                <mapping.RenderMetadata {...timelineLog} />
              ) : (
                timelineLog.action
              )}
            </span>
          </p>
          {mapping && mapping.RenderChildren && (
            <Button
              minimal
              small
              intent="info"
              display="inline-block"
              marginLeft="auto"
              marginTop="-4px"
              onClick={() => setShowDetails((s) => !s)}
            >
              {showDetails ? (
                <FormattedMessage defaultMessage="Hide" />
              ) : (
                <FormattedMessage defaultMessage="Show more" />
              )}
            </Button>
          )}
        </Box>
        <p className="small muted">
          <FormattedDate value={timelineLog.timestamp} format="default" />{' '}
          <FormattedTime value={timelineLog.timestamp} format="default" />
        </p>
        {showDetails && mapping.RenderChildren && (
          <Box marginTop={minorScale(1)}>
            <mapping.RenderChildren {...timelineLog} />
          </Box>
        )}
      </Box>
    </Box>
  );
};
